/// <reference types="@types/google.maps" />
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Box, CircularProgress } from '@mui/material';
import LocationSearch from './components/LocationSearch/locationSearch';
import {
  loadGoogleMapsAsync,
  getStaticMapUrl,
  GOOGLE_MAPS_CONFIG,
} from '../../../../../utils/googleMapsLoader';
import type { LocationData } from '../../types';

export type { LocationData };

interface GoogleMapStandaloneProps {
  onLocationChange: (location: LocationData) => void;
  initialLocation?: LocationData;
}

const GoogleMapStandalone: React.FC<GoogleMapStandaloneProps> = ({
  onLocationChange,
  initialLocation,
}) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<LocationData | null>(
    initialLocation || null,
  );

  const zoomMap = 18;

  // Actualizar la ubicación y notificar al padre
  const updateLocation = useCallback(
    (location: LocationData) => {
      setCurrentLocation(location);
      onLocationChange(location);
    },
    [onLocationChange],
  );

  // Cargar Google Maps una sola vez
  useEffect(() => {
    let mounted = true;

    const initializeGoogleMaps = async () => {
      try {
        await loadGoogleMapsAsync(GOOGLE_MAPS_CONFIG.apiKey, GOOGLE_MAPS_CONFIG.libraries);

        if (!mounted) return;

        // Verificar que todas las APIs estén disponibles
        if (!window.google?.maps?.Map || !window.google?.maps?.Geocoder) {
          console.error('Google Maps APIs no están completamente disponibles');
          setIsLoading(false);
          return;
        }

        // Inicializar el geocoder aquí para usarlo después
        if (window.google?.maps?.Geocoder) {
          geocoderRef.current = new window.google.maps.Geocoder();
        }

        setIsLoading(false);
        // La inicialización del mapa se hará en el efecto que observa isLoading
      } catch (error) {
        console.error('Error al cargar Google Maps:', error);
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    initializeGoogleMaps();

    // Obtener ubicación del usuario si no hay una seleccionada
    if (!initialLocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const latitude = position.coords.latitude;
          const longitude = position.coords.longitude;
          const latitudeString = latitude.toString();
          const longitudeString = longitude.toString();

          const staticMapUrl = getStaticMapUrl(latitude, longitude, zoomMap);

          updateLocation({
            lat: latitudeString,
            lng: longitudeString,
            title: '',
            description: '',
            url: staticMapUrl,
          });
        },
        (error) => {
          console.error('Error getting location:', error);
          // Ubicación por defecto (Buenos Aires, Argentina)
          const defaultLat = -34.6037;
          const defaultLng = -58.3816;
          updateLocation({
            lat: defaultLat.toString(),
            lng: defaultLng.toString(),
            title: '',
            description: '',
            url: getStaticMapUrl(defaultLat, defaultLng, zoomMap),
          });
        },
      );
    }
  }, [initialLocation, updateLocation, zoomMap]);

  // Inicializar el mapa cuando Google Maps esté cargado y el contenedor esté en el DOM
  // Este efecto se ejecuta cuando isLoading cambia a false, momento en que el div del mapa
  // ya está renderizado y mapContainerRef.current está disponible
  useEffect(() => {
    if (isLoading || mapRef.current) return;
    const timeout = setTimeout(() => {
      if (!mapRef.current) {
        initializeMap();
      }
    }, 50);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  // Inicializar el mapa cuando los datos estén disponibles
  useEffect(() => {
    if (mapLoaded && currentLocation) {
      updateMapLocation();
    }
  }, [mapLoaded, currentLocation]);

  // Limpiar al desmontar
  useEffect(() => {
    return () => {
      if (markerRef.current) {
        markerRef.current.setMap(null);
        markerRef.current = null;
      }
    };
  }, []);

  // Función para inicializar el mapa
  const initializeMap = () => {
    if (!mapContainerRef.current) {
      console.warn('Contenedor del mapa no disponible');
      return;
    }

    if (!window.google?.maps) {
      console.warn('Google Maps API no disponible aún');
      return;
    }

    try {
      const mapOptions = {
        zoom: zoomMap,
        disableDefaultUI: false,
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      };

      // Crear instancia del mapa
      mapRef.current = new window.google.maps.Map(
        mapContainerRef.current,
        mapOptions as google.maps.MapOptions,
      );

      // Añadir event listener para clicks en el mapa
      mapRef.current.addListener('click', (event: google.maps.MapMouseEvent) => {
        handleMapClick(event);
      });

      setMapLoaded(true);
    } catch (error) {
      console.error('Error inicializando mapa:', error);
    }
  };

  // Manejar clicks en el mapa
  const handleMapClick = (event: google.maps.MapMouseEvent) => {
    if (!event.latLng || !geocoderRef.current) return;

    const position = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng(),
    };

    // Actualizar el marcador primero para respuesta inmediata
    if (markerRef.current) {
      markerRef.current.setPosition(position);
    } else if (mapRef.current) {
      // @ts-ignore - Google Maps types inconsistency
      markerRef.current = new window.google.maps.Marker({
        position: position,
        map: mapRef.current,
      });
    }

    // Usar geocodificación inversa para obtener la dirección
    geocoderRef.current.geocode({ location: position }, (results, status) => {
      if (status === 'OK' && results && results[0]) {
        const address = results[0].formatted_address;
        const staticMapUrl = getStaticMapUrl(position.lat, position.lng, zoomMap);

        updateLocation({
          lat: position.lat.toString(),
          lng: position.lng.toString(),
          title: address || '',
          description: '',
          url: staticMapUrl,
        });
      } else {
        // Si falla la geocodificación, al menos guardamos las coordenadas
        const staticMapUrl = getStaticMapUrl(position.lat, position.lng, zoomMap);

        updateLocation({
          lat: position.lat.toString(),
          lng: position.lng.toString(),
          title: `${position.lat.toFixed(6)}, ${position.lng.toFixed(6)}`,
          description: '',
          url: staticMapUrl,
        });
      }
    });
  };

  // Actualizar la ubicación en el mapa
  const updateMapLocation = () => {
    if (!mapRef.current || !currentLocation) return;

    try {
      const position = {
        lat: parseFloat(currentLocation.lat),
        lng: parseFloat(currentLocation.lng),
      };

      // Centrar el mapa
      mapRef.current.setCenter(position);

      // Actualizar o crear el marcador
      if (markerRef.current) {
        markerRef.current.setPosition(position);
      } else {
        // @ts-ignore - Google Maps types inconsistency
        markerRef.current = new window.google.maps.Marker({
          position: position,
          map: mapRef.current,
        });
      }
    } catch (error) {
      console.error('Error actualizando mapa:', error);
    }
  };

  // Manejar selección desde LocationSearch
  const handlePlaceSelected = (coords: google.maps.LatLngLiteral, address?: string) => {
    const staticMapUrl = getStaticMapUrl(coords.lat, coords.lng, zoomMap);

    updateLocation({
      lat: coords.lat.toString(),
      lng: coords.lng.toString(),
      title: address || '',
      description: '',
      url: staticMapUrl,
    });
  };

  if (isLoading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', height: 'calc(100vh - 305px)' }}>
      <LocationSearch onPlaceSelected={handlePlaceSelected} />
      <div style={{ height: '100%' }} ref={mapContainerRef} />
    </Box>
  );
};

export default GoogleMapStandalone;
